import type { OfflineProfile } from '@jq4race/shared';

// Design D11 / decision #13 (product/pwa-offline-first-decisions): the
// offline profile is valid for 30 days, mirroring the refresh cookie's
// maxAge — the device shouldn't be able to "stay logged in offline" longer
// than an actually-online session could anyway.
export const OFFLINE_PROFILE_TTL_DAYS = 30;
const TTL_MS = OFFLINE_PROFILE_TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Pure TTL check — no Dexie/IO here so it is trivially unit-testable and
 * reusable from both the beforeLoad gate (below) and any future UI that
 * needs to show "profile expires soon".
 *
 * A profile saved in the future relative to `now` is also treated as
 * invalid — a clock-skew guard against ever trusting a profile whose
 * `savedAt` couldn't have happened yet.
 */
export function isOfflineProfileValid(profile: OfflineProfile | null, now: Date): boolean {
  if (!profile) return false;
  const savedAt = new Date(profile.savedAt).getTime();
  const nowMs = now.getTime();
  if (savedAt > nowMs) return false;
  return nowMs - savedAt < TTL_MS;
}

export type AuthGateResult = 'proceed' | 'redirect-login' | 'offline-shell';

// Mirrors packages/api-client's BootstrapOutcome['kind'] — duplicated as a
// plain string union (rather than importing the type) so this pure domain
// module has zero build-time coupling to @jq4race/api-client; the caller
// (_authenticated.tsx) is responsible for mapping one to the other.
export type AuthBootOutcomeKind = 'session' | 'unauthenticated' | 'unreachable';

export interface ResolveAuthGateInput {
  /** Whether `authStore.get()` currently returns a non-null in-memory session. */
  hasSession: boolean;
  /**
   * WHY bootstrapSession() (main.tsx) did not recover a session this page
   * load — see packages/api-client's BootstrapOutcome /
   * classifyBootstrapFailure(). Adversarial-review fix (Slice 3, finding #2
   * + its own follow-up review): this REPLACES the previous `isOnline`
   * (`navigator.onLine`) signal, which is devtools-spoofable and does not
   * actually prove a request failed for network reasons. `'unreachable'`
   * (fetch rejected, the 8s boot timeout, ANY 5xx, or 429 — see
   * packages/api-client) may unlock the offline-shell branch below — for an
   * operator, a Railway outage is exactly as unusable as having no signal.
   * `'unauthenticated'` (a real 401/403, or conservatively any other 4xx)
   * always redirects to login: the server is healthy and either explicitly
   * rejected the session or answered with something that is not a
   * connectivity problem.
   */
  bootOutcomeKind: AuthBootOutcomeKind;
  /** The offline profile read from Dexie kv, or null if none was ever saved. */
  profile: OfflineProfile | null;
  now: Date;
}

/**
 * Pure decision for `_authenticated.tsx`'s beforeLoad (task 3.6). Kept
 * outside the route file so the online-vs-offline branching logic — which
 * IS a real product decision, not routing plumbing — is unit-testable
 * without mounting the router.
 *
 * - A session in memory always wins (normal online path, or an operator who
 *   never lost their in-memory session even though the device is currently
 *   offline mid-shift).
 * - No session + `bootOutcomeKind === 'unauthenticated'` = the server is
 *   healthy and either explicitly rejected the session or answered with a
 *   non-connectivity 4xx → send to /login, same as today. Applies even if a
 *   valid cached profile exists.
 * - No session + `bootOutcomeKind === 'unreachable'` + a valid cached
 *   profile = the "offline reopen" scenario from spec §pwa-offline-shell →
 *   render the shell from the profile instead of bouncing to a /login the
 *   device (or the backend itself, during an outage) can't even reach.
 * - No session + unreachable + no/expired profile = nothing to render
 *   offline with → /login (will itself fail until connectivity/the backend
 *   returns, but that's the correct, honest state — never fabricate a
 *   session).
 */
export function resolveAuthGate({
  hasSession,
  bootOutcomeKind,
  profile,
  now,
}: ResolveAuthGateInput): AuthGateResult {
  if (hasSession) return 'proceed';
  if (bootOutcomeKind !== 'unreachable') return 'redirect-login';
  return isOfflineProfileValid(profile, now) ? 'offline-shell' : 'redirect-login';
}
